import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  ChevronDownIcon,
  FormField,
  Input,
  PrimaryButton,
  SecondaryButton,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { fetchAdminLeads } from "../utils/adminApi";

const DEFAULT_LIMIT = 50;
const COLUMNS_STORAGE_KEY = "admin-leads-visible-columns-v1";

const formatIsoDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
};

const LEAD_SITUATION_OPTIONS = [
  "LANDING_EMAIL",
  "PRICING",
  "ENTERPRISE",
  "PARTNERSHIP",
  "SUBSCRIPTION_UPGRADE_INTEREST",
  "SUPPORT",
  "OTHER",
].map((value) => ({ value, label: value.replace(/_/g, " ") }));

const COLUMN_DEFS = [
  { key: "id", label: "ID" },
  { key: "created_at", label: "Created" },
  { key: "updated_at", label: "Updated" },
  { key: "user", label: "User" },
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "celphone", label: "Celphone" },
  { key: "situation", label: "Situation" },
  { key: "info", label: "Info" },
  { key: "message", label: "Message" },
  { key: "instagram_url", label: "Instagram" },
  { key: "facebook_url", label: "Facebook" },
  { key: "linkedin_url", label: "LinkedIn" },
];

const buildDefaultVisibleColumns = () => {
  const defaultHidden = new Set(["instagram_url", "facebook_url", "linkedin_url"]);
  const defaults = {};
  COLUMN_DEFS.forEach((c) => {
    defaults[c.key] = !defaultHidden.has(c.key);
  });
  return defaults;
};

const loadVisibleColumnsFromStorage = () => {
  const defaults = buildDefaultVisibleColumns();
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;

    // Only allow known keys; default missing keys to true.
    const next = { ...defaults };
    Object.keys(parsed).forEach((k) => {
      if (k in defaults) next[k] = !!parsed[k];
    });
    return next;
  } catch {
    return defaults;
  }
};

const useOutsideClick = (ref, handler, when = true) => {
  useEffect(() => {
    if (!when) return undefined;
    const onPointerDown = (e) => {
      const el = ref?.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      handler?.(e);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [ref, handler, when]);
};

const LeadsAdminPage = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    user: "",
    email: "",
    name: "",
    celphone: "",
    info: "",
    situations: [],
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [leads, setLeads] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [visibleColumns, setVisibleColumns] = useState(() => loadVisibleColumnsFromStorage());
  const [situationsOpen, setSituationsOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const situationsDropdownRef = useRef(null);
  const columnsDropdownRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns || {}));
    } catch {
      // ignore
    }
  }, [visibleColumns]);

  const displayedColumns = useMemo(() => {
    const vis = visibleColumns || {};
    return COLUMN_DEFS.filter((c) => vis[c.key]);
  }, [visibleColumns]);

  const hasAnyFilter = useMemo(() => {
    return Object.values(appliedFilters || {}).some((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return String(v || "").trim().length > 0;
    });
  }, [appliedFilters]);

  const loadLeads = async ({ reset = false, cursor = null } = {}) => {
    if (reset) {
      setLoading(true);
      setError("");
    } else {
      setLoadingMore(true);
      setError("");
    }

    try {
      const data = await fetchAdminLeads({
        cursor,
        limit: DEFAULT_LIMIT,
        user: (appliedFilters.user || "").trim(),
        email: (appliedFilters.email || "").trim(),
        name: (appliedFilters.name || "").trim(),
        celphone: (appliedFilters.celphone || "").trim(),
        info: (appliedFilters.info || "").trim(),
        situations: appliedFilters.situations || [],
      });

      const results = data?.results || [];
      const next = data?.next_cursor || null;

      if (reset) setLeads(results);
      else setLeads((prev) => [...(prev || []), ...results]);

      setNextCursor(next);
    } catch (err) {
      setError(err?.message || "Failed to load leads");
      if (reset) {
        setLeads([]);
        setNextCursor(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadLeads({ reset: true, cursor: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const onApply = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setAppliedFilters({
      ...filters,
      situations: [...(filters.situations || [])],
    });
  };

  const onReset = () => {
    const cleared = { user: "", email: "", name: "", celphone: "", info: "", situations: [] };
    setFilters(cleared);
    setAppliedFilters(cleared);
  };

  const toggleSituation = (value) => {
    const v = String(value || "").trim();
    if (!v) return;
    setFilters((p) => {
      const set = new Set([...(p?.situations || [])]);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      return { ...p, situations: Array.from(set) };
    });
  };

  const setAllSituations = (checked) => {
    setFilters((p) => ({
      ...p,
      situations: checked ? LEAD_SITUATION_OPTIONS.map((o) => o.value) : [],
    }));
  };

  const toggleColumn = (key) => {
    setVisibleColumns((p) => ({ ...(p || {}), [key]: !(p || {})[key] }));
  };

  const setAllColumns = (checked) => {
    if (checked) setVisibleColumns(buildDefaultVisibleColumns());
    else {
      const none = {};
      COLUMN_DEFS.forEach((c) => {
        none[c.key] = false;
      });
      setVisibleColumns(none);
    }
  };

  const closeAllDropdowns = () => {
    setSituationsOpen(false);
    setColumnsOpen(false);
  };

  useOutsideClick(
    situationsDropdownRef,
    () => {
      setSituationsOpen(false);
    },
    situationsOpen
  );
  useOutsideClick(
    columnsDropdownRef,
    () => {
      setColumnsOpen(false);
    },
    columnsOpen
  );

  useEffect(() => {
    if (!situationsOpen && !columnsOpen) return undefined;
    const onKeyDown = (e) => {
      if (e?.key === "Escape") closeAllDropdowns();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [situationsOpen, columnsOpen]);

  const renderUrl = (url) => {
    const u = (url || "").trim();
    if (!u) return <span className={textColors.muted}>—</span>;
    return (
      <a
        href={u}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline ${textColors.primary}`}
      >
        Open
      </a>
    );
  };

  const isColumnVisible = (key) => {
    return !!(visibleColumns || {})[key];
  };

  const selectedSituationsCount = (filters?.situations || []).length;
  const situationsSummary = useMemo(() => {
    const count = selectedSituationsCount;
    if (count === 0) return "Any";
    if (count === LEAD_SITUATION_OPTIONS.length) return "All";
    return `${count} selected`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSituationsCount]);

  const displayedColumnsCount = displayedColumns.length;
  const columnsSummary = useMemo(() => {
    if (displayedColumnsCount === 0) return "None";
    if (displayedColumnsCount === COLUMN_DEFS.length) return "All";
    return `${displayedColumnsCount} selected`;
  }, [displayedColumnsCount]);

  const renderLeadValue = (lead, key, variant = "table") => {
    const u = lead?.user || null;
    const userLabel = u ? `${u.username || "User"} (#${u.id})` : "—";

    const isCard = variant === "card";
    const longTextClass = isCard
      ? "break-words whitespace-pre-wrap line-clamp-4"
      : "max-w-[520px] truncate";
    const longInfoClass = isCard
      ? "break-words whitespace-pre-wrap line-clamp-3"
      : "max-w-[420px] truncate";

    switch (key) {
      case "id":
        return lead?.id ?? "—";
      case "created_at":
        return formatIsoDateTime(lead?.created_at);
      case "updated_at":
        return formatIsoDateTime(lead?.updated_at);
      case "user":
        return u ? (
          <button
            type="button"
            className={[
              `underline ${textColors.primary}`,
              isCard ? "block min-w-0 truncate text-left" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={isCard ? userLabel : undefined}
            onClick={() => navigate(`/admin/dashboard/users/${u.id}`)}
          >
            {userLabel}
          </button>
        ) : (
          <span className={textColors.muted}>—</span>
        );
      case "email":
        return isCard ? (
          <div className="min-w-0 truncate" title={lead?.email || ""}>
            {lead?.email || "—"}
          </div>
        ) : (
          lead?.email || "—"
        );
      case "name":
        return isCard ? (
          <div className="min-w-0 truncate" title={lead?.name || ""}>
            {lead?.name || "—"}
          </div>
        ) : (
          lead?.name || "—"
        );
      case "celphone":
        return isCard ? (
          <div className="min-w-0 truncate" title={lead?.celphone || ""}>
            {lead?.celphone || "—"}
          </div>
        ) : (
          lead?.celphone || "—"
        );
      case "situation":
        return isCard ? (
          <div className="min-w-0 truncate" title={lead?.situation || ""}>
            {lead?.situation || "—"}
          </div>
        ) : (
          lead?.situation || "—"
        );
      case "info":
        return (
          <div className={longInfoClass} title={lead?.info || ""}>
            {lead?.info || "—"}
          </div>
        );
      case "message":
        return (
          <div className={longTextClass} title={lead?.message || ""}>
            {lead?.message || "—"}
          </div>
        );
      case "instagram_url":
        return renderUrl(lead?.instagram_url);
      case "facebook_url":
        return renderUrl(lead?.facebook_url);
      case "linkedin_url":
        return renderUrl(lead?.linkedin_url);
      default:
        return "—";
    }
  };

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className={`${typography.h2} ${textColors.primary}`}>Leads</h1>
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Browse potential customer leads with filters, column visibility controls, and cursor pagination.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <SecondaryButton
              onClick={() => loadLeads({ reset: true, cursor: null })}
              disabled={loading}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </SecondaryButton>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border border-red-300 bg-red-50 text-red-700 px-4 py-3">
            {error}
          </div>
        )}

        <Card className="mb-6">
          <CardHeader title="Filters" subtitle={hasAnyFilter ? "Filters applied" : "No filters applied"} />
          <CardBody>
            <form onSubmit={onApply} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <FormField label="User" htmlFor="filter_user">
                  <Input
                    id="filter_user"
                    type="text"
                    value={filters.user}
                    onChange={(e) => setFilters((p) => ({ ...p, user: e.target.value }))}
                    placeholder="id / username / email"
                  />
                </FormField>

                <FormField label="Email" htmlFor="filter_email">
                  <Input
                    id="filter_email"
                    type="text"
                    value={filters.email}
                    onChange={(e) => setFilters((p) => ({ ...p, email: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>

                <FormField label="Name" htmlFor="filter_name">
                  <Input
                    id="filter_name"
                    type="text"
                    value={filters.name}
                    onChange={(e) => setFilters((p) => ({ ...p, name: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>

                <FormField label="Celphone" htmlFor="filter_celphone">
                  <Input
                    id="filter_celphone"
                    type="text"
                    value={filters.celphone}
                    onChange={(e) => setFilters((p) => ({ ...p, celphone: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>

                <FormField label="Info" htmlFor="filter_info">
                  <Input
                    id="filter_info"
                    type="text"
                    value={filters.info}
                    onChange={(e) => setFilters((p) => ({ ...p, info: e.target.value }))}
                    placeholder="contains…"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Situation" htmlFor="filter_situations">
                  <div className="relative" ref={situationsDropdownRef}>
                    <SecondaryButton
                      id="filter_situations"
                      type="button"
                      fullWidth
                      className="justify-between whitespace-nowrap"
                      aria-label={`Situation (${situationsSummary})`}
                      aria-haspopup="menu"
                      aria-expanded={situationsOpen}
                      onClick={() => {
                        setSituationsOpen((v) => !v);
                        setColumnsOpen(false);
                      }}
                      rightIcon={<ChevronDownIcon size="sm" />}
                    >
                      <span className="flex-1 min-w-0 text-left truncate" title={situationsSummary}>
                        {situationsSummary}
                      </span>
                    </SecondaryButton>

                    {situationsOpen && (
                      <div
                        role="menu"
                        className={[
                          "absolute left-0 right-0 mt-2 z-50",
                          "rounded-2xl border shadow-lg",
                          borders.glass,
                          surfaces.glassCard,
                          "p-3",
                        ].join(" ")}
                      >
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center mb-3">
                          <Checkbox
                            checked={selectedSituationsCount === LEAD_SITUATION_OPTIONS.length}
                            indeterminate={
                              selectedSituationsCount > 0 &&
                              selectedSituationsCount < LEAD_SITUATION_OPTIONS.length
                            }
                            onChange={(e) => setAllSituations(e.target.checked)}
                            label="All"
                          />
                          <SecondaryButton
                            type="button"
                            onClick={() => setAllSituations(false)}
                            className="w-full sm:w-auto whitespace-nowrap"
                          >
                            Clear
                          </SecondaryButton>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-auto pr-1">
                          {LEAD_SITUATION_OPTIONS.map((opt) => (
                            <Checkbox
                              key={opt.value}
                              checked={(filters.situations || []).includes(opt.value)}
                              onChange={() => toggleSituation(opt.value)}
                              label={opt.label}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </FormField>

                <FormField label="Columns" htmlFor="filter_columns">
                  <div className="relative" ref={columnsDropdownRef}>
                    <SecondaryButton
                      id="filter_columns"
                      type="button"
                      fullWidth
                      className="justify-between whitespace-nowrap"
                      aria-label={`Columns (${columnsSummary})`}
                      aria-haspopup="menu"
                      aria-expanded={columnsOpen}
                      onClick={() => {
                        setColumnsOpen((v) => !v);
                        setSituationsOpen(false);
                      }}
                      rightIcon={<ChevronDownIcon size="sm" />}
                    >
                      <span className="flex-1 min-w-0 text-left truncate" title={columnsSummary}>
                        {columnsSummary}
                      </span>
                    </SecondaryButton>

                    {columnsOpen && (
                      <div
                        role="menu"
                        className={[
                          "absolute left-0 right-0 mt-2 z-50",
                          "rounded-2xl border shadow-lg",
                          borders.glass,
                          surfaces.glassCard,
                          "p-3",
                        ].join(" ")}
                      >
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center mb-3">
                          <Checkbox
                            checked={displayedColumns.length === COLUMN_DEFS.length}
                            indeterminate={
                              displayedColumns.length > 0 && displayedColumns.length < COLUMN_DEFS.length
                            }
                            onChange={(e) => setAllColumns(e.target.checked)}
                            label="All"
                          />
                          <SecondaryButton
                            type="button"
                            onClick={() => setAllColumns(false)}
                            className="w-full sm:w-auto whitespace-nowrap"
                          >
                            Hide all
                          </SecondaryButton>
                          <SecondaryButton
                            type="button"
                            onClick={() => setVisibleColumns(buildDefaultVisibleColumns())}
                            className="w-full sm:w-auto whitespace-nowrap"
                          >
                            Reset
                          </SecondaryButton>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-auto pr-1">
                          {COLUMN_DEFS.map((col) => (
                            <Checkbox
                              key={col.key}
                              checked={!!(visibleColumns || {})[col.key]}
                              onChange={() => toggleColumn(col.key)}
                              label={col.label}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </FormField>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <PrimaryButton type="submit" disabled={loading} className="w-full sm:w-auto whitespace-nowrap">
                  Apply
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={onReset}
                  disabled={loading}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  Reset
                </SecondaryButton>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader
            title="All Leads"
            subtitle={`Loaded: ${leads.length}${nextCursor ? " • More available" : ""}`}
          />
          <CardBody>
            {loading ? (
              <div className={`${typography.body} ${textColors.secondary}`}>Loading leads…</div>
            ) : leads.length === 0 ? (
              <div className={`${typography.body} ${textColors.secondary}`}>No leads found.</div>
            ) : displayedColumns.length === 0 ? (
              <div className={`${typography.body} ${textColors.secondary}`}>No columns selected.</div>
            ) : (
              <>
                <div className="space-y-3 lg:hidden">
                    {leads.map((lead) => {
                      const email = (lead?.email || "").trim();
                      const name = (lead?.name || "").trim();
                      const situation = (lead?.situation || "").trim();

                      const title =
                        (isColumnVisible("email") && email) ||
                        (isColumnVisible("name") && name) ||
                        (isColumnVisible("id") && lead?.id != null ? `Lead #${lead.id}` : "Lead");

                      const subtitleParts = [];
                      if (isColumnVisible("name") && title !== name && name) subtitleParts.push(name);
                      if (isColumnVisible("id") && lead?.id != null && title !== `Lead #${lead.id}`) {
                        subtitleParts.push(`#${lead.id}`);
                      }
                      if (isColumnVisible("created_at")) subtitleParts.push(formatIsoDateTime(lead?.created_at));
                      const subtitle = subtitleParts.filter(Boolean).join(" • ");

                      const headerKeys = new Set();
                      // Keys shown in the card header (avoid duplicating in the details grid).
                      if (isColumnVisible("email") && email && title === email) headerKeys.add("email");
                      if (isColumnVisible("name") && name && (title === name || subtitleParts.includes(name)))
                        headerKeys.add("name");
                      if (isColumnVisible("id") && lead?.id != null && subtitleParts.some((p) => p === `#${lead.id}`))
                        headerKeys.add("id");
                      if (isColumnVisible("created_at")) headerKeys.add("created_at");
                      if (isColumnVisible("situation") && situation) headerKeys.add("situation");

                      const detailColumns = displayedColumns.filter((c) => !headerKeys.has(c.key));

                      const cardSpanClass = (key) => {
                        if (key === "message" || key === "info") return "sm:col-span-2";
                        if (key.endsWith("_url")) return "sm:col-span-2";
                        return "";
                      };

                      return (
                        <div
                          key={lead.id}
                          className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div
                                className={`${typography.body} ${textColors.primary} font-medium truncate`}
                                title={title || "Lead"}
                              >
                                {title || "Lead"}
                              </div>
                              {subtitle ? (
                                <div className={`${typography.caption} ${textColors.muted} mt-0.5`}>{subtitle}</div>
                              ) : null}
                            </div>
                            {isColumnVisible("situation") && situation ? (
                              <div
                                className={[
                                  "shrink-0",
                                  "px-2 py-1 rounded-2xl",
                                  "border",
                                  typography.caption,
                                  surfaces.glassCard,
                                  borders.glass,
                                  textColors.secondary,
                                ].join(" ")}
                              >
                                {situation}
                              </div>
                            ) : null}
                          </div>

                          {detailColumns.length > 0 ? (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {detailColumns.map((col) => (
                                <div key={col.key} className={cardSpanClass(col.key)}>
                                  <div className={`${typography.caption} ${textColors.muted}`}>{col.label}</div>
                                  <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5 min-w-0`}>
                                    {renderLeadValue(lead, col.key, "card")}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                </div>

                <div className="hidden lg:block overflow-x-auto">
                    <table className={`w-full text-left ${typography.bodySmall}`}>
                      <thead>
                        <tr className={`border-b ${borders.glass}`}>
                          {displayedColumns.map((col) => (
                            <th key={col.key} className="py-2 pr-4">
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr key={lead.id} className={`border-b ${borders.glass}`}>
                            {displayedColumns.map((col) => (
                              <td key={col.key} className="py-2 pr-4 align-top">
                                {renderLeadValue(lead, col.key, "table")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              </>
            )}

            {nextCursor && (
              <div className="mt-4 flex items-center justify-center">
                <SecondaryButton
                  onClick={() => loadLeads({ reset: false, cursor: nextCursor })}
                  disabled={loadingMore}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </SecondaryButton>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LeadsAdminPage;

